"use client";
import React, { useState, useEffect } from "react";
import { Container } from "@/src/components/container";
import FilteringSection, { Filters, FilterType } from "./FilteringSection";
import JobCard from "@/src/components/cards/Jobs";
import { JobCardProps } from "@/src/components/cards/Jobs";
import axiosConfig from "@/src/utils/axiosConfig.ts";
import Lottie from 'lottie-web';
import animationData from '../../../public/assets/animations/no-jobs.json';

export interface JobsSectionProps {
  sectionHeading: {
    subtitle: string;
    title: string;
  };
  lang: string;
}

export function JobsSection({ sectionHeading, lang }: JobsSectionProps) {
  const isArabic = lang === "ar";
  const [filters, setFilters] = useState<Filters>({
    language: "",
    postDate: "",
    jobType: "",
    specialization: "",
    contractType: "",
    status: "",
    level: "",
    departmentId: "",
  });
  const [jobs, setJobs] = useState<JobCardProps[]>([]);
  const [loading, setLoading] = useState(false); // Optional: Loading indicator

  const handleFilterChange = (filterName: FilterType, selectedOption: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: selectedOption,
    }));
  };

  useEffect(() => {
    Lottie.loadAnimation({
      container: document.querySelector('#lottie'),
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: animationData
    })
  }, [])

  useEffect(() => {
    const fetchFilteredJobs = async () => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value) queryParams.append(key, value);
        });

        const response = await axiosConfig.get(`/api/Job?${queryParams.toString()}`
        );
        const responseData = await response.data

        if (responseData?.Data) {
          setJobs(
            responseData.Data.map((job: any) => ({
              jobTitle: job.Name,
              description: job.PositionSummary,
              jobType: job.ContractType,
              postDate: job.ClosingDate,
              isAvailable: job.Status === 0,
              keyWords: job.JobSkills.map((skill: any) => skill.Name),
              jobId: job.Id,
              IsRequesterPreviouslyApplied: job.IsRequesterPreviouslyApplied,
            }))
          );
        } else {
          setJobs([]);
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredJobs();
  }, [filters]);

  const handleResetFilters = () => {
    setFilters({
      jobType: "",
      specialization: "",
      language: "",
      postDate: "",
      contractType: "",
      status: "",
      level: "",
      departmentId: "",
    });
  };

  return (
    <section className="section-padding-primary overflow-hidden">
      <Container>
        <h1 className="text-[#1f2835] dark:text-white text-[22px] font-semibold leading-[30.80px] my-6">
          {isArabic ? " الوظائف المتاحة" : "Available Jobs "} ({jobs?.length || 0})
        </h1>
        <FilteringSection
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
        />
          <div className={`my-8 mx-auto ${jobs.length > 0 ? 'w-0 h-0 hidden': ''}`}>
          <div className="flex justify-center items-center relative overflow-hidden">
            <span className="w-[200px] h-[200px]" id="lottie"></span>
          </div>
          <h3 className="mt-3 text-center text-lg text-bold">{isArabic ? "لا توجد وظائف متاحة" : "No available jobs"}</h3>
        </div>
        {loading ? (
          <p>{isArabic ? "جارٍ التحميل..." : "Loading..."}</p>
        ) : 
        jobs.length > 0 ?
        (
          <div className="flex flex-wrap gap-6 items-center justify-center sm:justify-start md:justify-center lg:justify-start">
            {jobs.map((job) => (
              <JobCard key={job.jobId} {...job} />
            ))}
          </div>
        )
      :
      ''}
      </Container>
    </section>
  );
}
